import { useState } from 'react';
import { useWalletConnect } from '../hooks/useWalletConnect';
import { WalletSelectionModal } from '../components/WalletSelectionModal';

/**
 * Wallet Connect Button Component
 * Displays connect/disconnect button for Web3 wallet authentication
 * Shows user info when connected
 */
export function WalletConnectButton({ onShowToast }) {
  const { wallet, user, isConnecting, connectWallet, disconnectWallet, isLoggedIn, hasWallet } = useWalletConnect();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);

  const handleConnect = () => {
    // Show wallet selection modal instead of direct connect
    setShowWalletModal(true);
  };

  const handleWalletSelect = async (walletType) => {
    const result = await connectWallet(walletType);

    if (result.success) {
      onShowToast?.(result.user.message || 'Successfully connected!', 'success');
      // Reload to refresh voting power indicators
      setTimeout(() => window.location.reload(), 1000);
    } else {
      onShowToast?.(result.error || 'Failed to connect wallet', 'error');
    }
  };

  const handleDisconnect = () => {
    disconnectWallet();
  };

  // Not connected state
  if (!isLoggedIn) {
    return (
      <>
        <div className="t24-wallet-connect-button">
          {!hasWallet ? (
            <a
              href="https://metamask.io/download/"
              target="_blank"
              rel="noopener noreferrer"
              className="t24-btn t24-btn-wallet t24-btn-install-wallet"
            >
              <span className="t24-wallet-icon">🦊</span>
              <span>Install MetaMask</span>
            </a>
          ) : (
            <button
              onClick={handleConnect}
              disabled={isConnecting}
              className="t24-btn t24-btn-wallet"
              aria-label="Connect Wallet"
            >
              {isConnecting ? (
                <>
                  <span className="t24-spinner-small"></span>
                  <span>Connecting...</span>
                </>
              ) : (
                <>
                  <span className="t24-wallet-icon">👛</span>
                  <span>Connect Wallet</span>
                </>
              )}
            </button>
          )}
        </div>

        {/* Wallet Selection Modal */}
        <WalletSelectionModal
          isOpen={showWalletModal}
          onClose={() => setShowWalletModal(false)}
          onSelectWallet={handleWalletSelect}
        />
      </>
    );
  }

  // Connected state
  return (
    <div className="t24-wallet-connected">
      <button
        className="t24-wallet-connected-button"
        onClick={() => setShowDropdown(!showDropdown)}
        aria-label="Wallet menu"
      >
        <div className="t24-wallet-connected-avatar">
          <span className="t24-wallet-connected-icon">👤</span>
        </div>
        <div className="t24-wallet-connected-info">
          <div className="t24-wallet-connected-name">{user.name}</div>
          {wallet.connected && wallet.address && (
            <div className="t24-wallet-connected-address">
              {wallet.address.slice(0, 6)}...{wallet.address.slice(-4)}
            </div>
          )}
        </div>
        {user.hasRole && user.votingPower > 0 && (
          <div className="t24-wallet-voting-power-badge" title="Total voting power">
            ⚡ {user.votingPower}
          </div>
        )}
        <svg
          className={`t24-wallet-dropdown-arrow ${showDropdown ? 'open' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="currentColor"
        >
          <path d="M6 8L2 4h8z" />
        </svg>
      </button>

      {showDropdown && (
        <div className="t24-wallet-dropdown">
          <div className="t24-wallet-dropdown-section">
            <div className="t24-wallet-dropdown-label">Account</div>
            <div className="t24-wallet-dropdown-value">{user.name}</div>
          </div>

          {wallet.connected && wallet.address && (
            <div className="t24-wallet-dropdown-section">
              <div className="t24-wallet-dropdown-label">Wallet Address</div>
              <div className="t24-wallet-dropdown-address">
                <code>{wallet.address}</code>
                <button
                  className="t24-copy-button"
                  onClick={() => {
                    navigator.clipboard.writeText(wallet.address);
                    onShowToast?.('Address copied!', 'success');
                  }}
                  title="Copy address"
                >
                  📋
                </button>
              </div>
            </div>
          )}

          {user.hasRole ? (
            <div className="t24-wallet-dropdown-section t24-wallet-role-info">
              <div className="t24-wallet-dropdown-label">Governance Role</div>
              <div className="t24-wallet-dropdown-value t24-has-role">
                ✓ Active ({user.votingPower} voting power)
              </div>
            </div>
          ) : (
            <div className="t24-wallet-dropdown-section t24-wallet-role-info">
              <div className="t24-wallet-dropdown-label">Governance Role</div>
              <div className="t24-wallet-dropdown-value t24-no-role">
                ⚠ No role assigned
              </div>
              <div className="t24-wallet-dropdown-hint">
                Contact an admin to request a governance role
              </div>
            </div>
          )}

          <div className="t24-wallet-dropdown-divider"></div>

          <button
            onClick={handleDisconnect}
            className="t24-wallet-disconnect-button"
          >
            <span>🚪</span>
            <span>Disconnect</span>
          </button>
        </div>
      )}

      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div
          className="t24-wallet-dropdown-overlay"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
}
